// Client-side checks for the A2P 10DLC (business texting) forms. The backend
// re-validates everything (crm/a2p/validation.py); these only give instant
// feedback and must stay in step with it.

// IRS prefixes that have never been assigned (see validation.py for source).
const INVALID_EIN_PREFIXES = new Set([
  "00",
  "07",
  "08",
  "09",
  "17",
  "18",
  "19",
  "28",
  "29",
  "49",
  "69",
  "70",
  "78",
  "79",
  "89",
  "96",
  "97",
])

export function collapseWhitespace(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * Returns { value, error }. `value` is "XX-XXXXXXX" when valid, else the
 * trimmed input. `error` is a user-facing message or null.
 */
export function normalizeEin(input) {
  const raw = String(input ?? "").trim()
  if (!raw) {
    return { value: "", error: "Enter your EIN." }
  }
  if (/[^0-9\s-]/.test(raw)) {
    return {
      value: raw,
      error: "An EIN only contains digits, like 12-3456789.",
    }
  }
  const digits = raw.replace(/[^0-9]/g, "")
  if (digits.length !== 9) {
    return {
      value: raw,
      error: "An EIN has exactly 9 digits, like 12-3456789.",
    }
  }
  if (new Set(digits).size === 1) {
    return {
      value: raw,
      error: "That EIN isn't valid. Check your IRS EIN letter.",
    }
  }
  const prefix = digits.slice(0, 2)
  if (INVALID_EIN_PREFIXES.has(prefix)) {
    return {
      value: raw,
      error: `That EIN isn't valid (the IRS never issues EINs starting with ${prefix}). Check your IRS EIN letter.`,
    }
  }
  return { value: `${prefix}-${digits.slice(2)}`, error: null }
}

export const BUSINESS_TYPES = [
  "Co-operative",
  "Corporation",
  "Limited Liability Corporation",
  "Non-profit Corporation",
  "Partnership",
]

export const LEGAL_NAME_LABEL =
  "Legal business name (exactly as on your IRS EIN letter — CP 575 / 147C)"

export const LEGAL_NAME_HELP =
  "Copy it exactly, including LLC, Inc. or punctuation. Not a DBA or your brokerage's name."

export const EIN_HELP =
  "Your business's own 9-digit EIN from the IRS — not your brokerage's, and not a Social Security number."
